import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Package, ChevronRight, Filter } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import DashboardWrapper from '../../components/DashboardWrapper';
import api from '../../lib/api';
import { getStatusConfig, fmtPrice, fmtDate } from '../../lib/orderStatus';
import { useAuth } from '../../context/AuthContext';

const PartnerOrders = () => {
  const { activeOrg } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [stats, setStats] = useState(null);

  const currentOrgId = activeOrg?.org_id;
  const orgIdRef = useRef(currentOrgId);
  useEffect(() => { orgIdRef.current = currentOrgId; }, [currentOrgId]);

  // ── Multi-org : rechargement au changement d'organisation ──
  useEffect(() => {
    if (!currentOrgId) {
      setOrders([]);
      setStats(null);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    loadData(currentOrgId, controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId]);

  const loadData = async (orgId, signal) => {
    setLoading(true);
    try {
      const params = orgId ? { org_id: orgId } : {};
      const [ordersRes, statsRes] = await Promise.all([
        api.get('/partner/purchase-orders', { params, signal }),
        api.get('/partner/purchase-orders/stats', { params, signal }),
      ]);
      if (signal?.aborted || orgIdRef.current !== orgId) return;
      setOrders(ordersRes.data || []);
      setStats(statsRes.data);
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError' || signal?.aborted) return;
      if (orgIdRef.current !== orgId) return;
      console.error('Error loading partner orders:', err);
    } finally {
      if (!signal?.aborted && orgIdRef.current === orgId) setLoading(false);
    }
  };

  const filteredOrders = activeFilter === 'all'
    ? orders
    : orders.filter(o => o.status === activeFilter);

  const filterButtons = [
    { key: 'all', label: 'Toutes', count: orders.length },
    { key: 'pending', label: 'En attente', count: stats?.pending || 0 },
    { key: 'confirmed', label: 'Confirmées', count: (stats?.confirmed || 0) + (stats?.confirmed_modified || 0) },
    { key: 'cancelled', label: 'Annulées', count: stats?.cancelled || 0 },
  ];

  // "Confirmées" filter includes both confirmed and confirmed_modified
  const getFilteredOrders = () => {
    if (activeFilter === 'all') return orders;
    if (activeFilter === 'confirmed') return orders.filter(o => o.status === 'confirmed' || o.status === 'confirmed_modified');
    return orders.filter(o => o.status === activeFilter);
  };

  const displayOrders = getFilteredOrders();

  return (
    <DashboardWrapper>
      <div className="space-y-6 max-w-5xl" data-testid="partner-orders-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <FileText className="h-6 w-6 text-slate-600" />
          <div>
            <h1 className="text-xl font-bold text-slate-900">Commandes reçues</h1>
            <p className="text-sm text-slate-500">Gérez les bons de commande de vos clients</p>
          </div>
        </div>

        {/* KPI Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card>
              <CardContent className="pt-4 pb-3 px-4">
                <p className="text-xs font-medium text-slate-500">Total commandes</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="stat-total">{stats.total_orders}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3 px-4">
                <p className="text-xs font-medium text-amber-600">En attente</p>
                <p className="text-2xl font-bold text-amber-700" data-testid="stat-pending">{stats.pending}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3 px-4">
                <p className="text-xs font-medium text-slate-500">CA total</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="stat-revenue">{fmtPrice(stats.total_revenue)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3 px-4">
                <p className="text-xs font-medium text-red-600">Annulées</p>
                <p className="text-2xl font-bold text-red-700" data-testid="stat-cancelled">{stats.cancelled}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Status Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="h-4 w-4 text-slate-400" />
          {filterButtons.map(f => (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                activeFilter === f.key
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label} ({f.count})
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16 gap-2 text-sm text-slate-400">
            <div className="animate-spin h-5 w-5 border-2 border-slate-400 border-t-transparent rounded-full" />
            Chargement...
          </div>
        )}

        {/* Empty state */}
        {!loading && displayOrders.length === 0 && (
          <div className="text-center py-16">
            <Package className="h-12 w-12 text-slate-200 mx-auto mb-3" />
            <p className="text-sm text-slate-400">
              {activeFilter === 'all' ? 'Aucune commande reçue pour le moment' : 'Aucune commande dans ce filtre'}
            </p>
          </div>
        )}

        {/* Orders list */}
        {!loading && displayOrders.length > 0 && (
          <div className="space-y-3">
            {displayOrders.map(po => {
              const st = getStatusConfig(po.status);
              const Icon = st.icon;
              const totals = po.confirmed_totals || po.snapshot_totals || {};
              const userSnap = po.snapshot_user || {};
              const itemCount = (po.snapshot_items || []).length;

              return (
                <Link key={po.id} to={`/dashboard/partner/commandes/${po.id}`}
                  className="block rounded-xl border border-slate-200 bg-white hover:border-indigo-300 hover:shadow-md transition-all"
                  data-testid={`partner-po-card-${po.order_number}`}>
                  <div className="p-4 flex items-center gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold text-slate-900">{po.order_number}</span>
                        <Badge className={`text-[10px] ${st.color}`}>
                          <Icon className="h-3 w-3 mr-1" />
                          {st.label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        <span>{userSnap.nom_magasin || userSnap.email || 'Client'}</span>
                        <span>{itemCount} produit{itemCount > 1 ? 's' : ''}</span>
                        <span>{fmtDate(po.created_at)}</span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-slate-900 tabular-nums">{fmtPrice(totals.total)}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">HT</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </DashboardWrapper>
  );
};

export default PartnerOrders;
